/* [Harmonee AI Inc. — TRADE SECRET — proprietary; do not distribute. See NOTICE / LICENSE.] */
// PortfolioCommand — the real Command Center: the Portfolio Command Plan
// (projects, tiers, weekly focus, agent fleet, risks, decisions) rendered
// from the gated /api/portfolio endpoint. Replaces the fabricated demo
// Command Center. Purely presentational; no fetching here.
import React from 'react';
import SystemPulse from './SystemPulse';

const ENTITY_ORDER = ['Harmonee', 'OTV', 'SCORI', 'Real estate', 'Brand', 'Advisory', 'Personal', 'All'];

function TierBadge({ tier }) {
  if (tier == null) return <span className="hm-tag hm-port-tier hm-port-tier-none">Untiered</span>;
  const labels = { 1: 'T1 · You lead', 2: 'T2 · You approve', 3: 'T3 · Agents run', 4: 'T4 · Paused' };
  return <span className={`hm-tag hm-port-tier hm-port-tier-${tier}`}>{labels[tier]}</span>;
}

function currentWeek(weeks) {
  const today = new Date().toISOString().slice(0, 10);
  let match = weeks[0];
  for (const w of weeks) { if (w.week <= today) match = w; else break; }
  return match;
}

export default function PortfolioCommand({ data, status, onRetry, onAskAria }) {
  if (status === 'locked') {
    return (
      <div className="hm-port-state">
        <div className="page-title">Portfolio Command</div>
        <p className="hm-port-state-text">
          The Portfolio Command Plan is served only to unlocked sessions — it never
          ships in the public app bundle. Unlock the dashboard to load it.
        </p>
      </div>
    );
  }
  if (status === 'error') {
    return (
      <div className="hm-port-state">
        <div className="page-title">Portfolio Command</div>
        <p className="hm-port-state-text">
          Couldn't load the Portfolio Command Plan from the server. This view shows
          nothing rather than stale or sample data.
        </p>
        {onRetry && (
          <button type="button" className="hm-btn hm-btn-quiet" onClick={onRetry}>Retry</button>
        )}
      </div>
    );
  }
  if (!data) {
    return (
      <div className="hm-port-state" aria-busy="true">
        <div className="page-title">Portfolio Command</div>
        <p className="hm-port-state-text">Loading the plan of record…</p>
      </div>
    );
  }

  const week = currentWeek(data.weeks);
  const tier1 = data.tiers.find((t) => t.tier === 1);
  const urgentRisks = data.risks.filter((r) => r.urgent);
  const wave1 = data.fleet.waves.find((w) => w.wave === 1);
  const totalAgents = data.fleet.pods.reduce((n, p) => n + p.agents.length, 0);

  return (
    <>
      <div className="brief-header">
        <div className="brief-date">Portfolio Command · as of {data.asOf} · {data.horizon}</div>
        <div className="page-title">The plan of record<em>.</em></div>
        <div className="brief-subtitle">{data.source}</div>
      </div>

      <SystemPulse
        items={[
          { label: 'Tier 1 — you lead', value: tier1.projects.length, sub: tier1.time + ' of your time', status: 'important', title: 'Jump to tiers', onClick: () => document.querySelector('.hm-port-tiers')?.scrollIntoView({ behavior: 'smooth', block: 'start' }) },
          { label: 'Decisions open', value: data.decisions.length, sub: 'reply approved or change', status: 'needs-action', title: 'Jump to decisions', onClick: () => document.querySelector('.hm-port-decisions')?.scrollIntoView({ behavior: 'smooth', block: 'start' }) },
          { label: 'Risk flags', value: data.risks.length, sub: urgentRisks.length + ' need action this week', status: urgentRisks.length ? 'needs-action' : 'moderate', title: 'Jump to risks', onClick: () => document.querySelector('.hm-port-risks')?.scrollIntoView({ behavior: 'smooth', block: 'start' }) },
          { label: 'Agent fleet', value: `${wave1.agents}/${totalAgents}`, sub: 'wave 1 live · plan of record', status: 'aria', title: 'Jump to the fleet', onClick: () => document.querySelector('.hm-port-fleet')?.scrollIntoView({ behavior: 'smooth', block: 'start' }) },
        ]}
      />

      {/* Bottom line — the three things that decide fundability */}
      <section className="hm-port-section" aria-label="Bottom line">
        <div className="hm-port-section-title">Bottom line — three things through December 31</div>
        <div className="hm-port-bottomline">
          {data.bottomLine.map((b) => (
            <div key={b.n} className="hm-port-goal">
              <span className="hm-port-goal-n">{b.n}</span>
              <div>
                <div className="hm-port-goal-title">{b.title}</div>
                <div className="hm-port-goal-detail">{b.detail}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* This week */}
      <section className="hm-port-section hm-port-week" aria-label="This week">
        <div className="hm-port-section-title">Week of {week.label}</div>
        <div className="hm-port-week-grid">
          <div><span className="hm-port-k">Fixed</span><p>{week.events}</p></div>
          <div><span className="hm-port-k">Your focus</span><p className="hm-port-focus">{week.focus}</p></div>
          <div><span className="hm-port-k">Agents deliver</span><p>{week.agents}</p></div>
        </div>
      </section>

      {/* Tiers */}
      <section className="hm-port-section hm-port-tiers" aria-label="Priority tiers">
        <div className="hm-port-section-title">Priority tiers</div>
        <div className="hm-port-tier-grid">
          {data.tiers.map((t) => (
            <div key={t.tier} className={`hm-port-tier-card hm-port-tier-card-${t.tier}`}>
              <div className="hm-port-tier-head">
                <span className="hm-port-tier-label">Tier {t.tier} · {t.label}</span>
                <span className="hm-port-tier-time">{t.time}</span>
              </div>
              <div className="hm-port-tier-projects">{t.projects.join(' · ')}</div>
              <div className="hm-port-tier-why">{t.why}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Project inventory */}
      <section className="hm-port-section" aria-label="Project inventory">
        <div className="hm-port-section-title">Project inventory · {data.projects.length} active</div>
        <div className="hm-port-projects">
          {ENTITY_ORDER.filter((e) => data.projects.some((p) => p.entity === e)).map((entity) => (
            <div key={entity} className="hm-port-entity-group">
              <div className="hm-port-entity">{entity}</div>
              {data.projects.filter((p) => p.entity === entity).map((p) => (
                <div key={p.id} className="hm-port-project">
                  <div className="hm-port-project-head">
                    <span className="hm-port-project-id">{p.id}</span>
                    <span className="hm-port-project-name">{p.name}</span>
                    <TierBadge tier={p.tier} />
                  </div>
                  <div className="hm-port-project-status">{p.status}</div>
                  <div className="hm-port-project-next"><span className="hm-port-k">Next</span> {p.milestone}</div>
                </div>
              ))}
            </div>
          ))}
        </div>
      </section>

      {/* Agent fleet */}
      <section className="hm-port-section hm-port-fleet" aria-label="Agent fleet">
        <div className="hm-port-section-title">
          Agent fleet · {totalAgents} agents in {data.fleet.pods.length} pods
          <span className="hm-port-honesty">Plan of record — live telemetry not wired yet</span>
        </div>
        <p className="hm-port-fleet-summary">{data.fleet.summary}</p>
        <div className="hm-port-pods">
          {data.fleet.pods.map((pod) => (
            <div key={pod.id} className="hm-port-pod">
              <div className="hm-port-pod-head">
                <span className="hm-port-pod-name">Pod {pod.id} · {pod.name}</span>
                <span className="hm-port-pod-scope">{pod.scope}</span>
              </div>
              {pod.agents.map((a) => (
                <div key={a.name} className="hm-port-agent">
                  <div className="hm-port-agent-head">
                    <span className="hm-port-agent-name">{a.name}</span>
                    <span className={`hm-tag hm-port-model ${a.model.startsWith('Fable') ? 'hm-port-model-fable' : ''}`}>{a.model}</span>
                    <span className={`hm-tag hm-port-wave ${a.wave === 1 ? 'hm-port-wave-live' : ''}`}>{a.wave === 1 ? 'Wave 1 · live' : `Wave ${a.wave}`}</span>
                  </div>
                  <div className="hm-port-agent-job">{a.job}</div>
                  <div className="hm-port-agent-cadence">{a.cadence}</div>
                </div>
              ))}
            </div>
          ))}
        </div>
        <p className="hm-port-rules"><span className="hm-port-k">Standing rules</span> {data.fleet.standingRules}</p>
      </section>

      {/* Risks */}
      <section className="hm-port-section hm-port-risks" aria-label="Risk and legal flags">
        <div className="hm-port-section-title">Risk and legal flags · {urgentRisks.length} need action this week</div>
        {data.risks.map((r) => (
          <div key={r.n} className={`hm-port-risk ${r.urgent ? 'hm-port-risk-urgent' : ''}`}>
            <div className="hm-port-risk-head">
              <span className="hm-port-risk-name">{r.n}. {r.risk}</span>
              {r.urgent && <span className="hm-tag hm-tag-urgency-high">This week</span>}
            </div>
            <div className="hm-port-risk-why">{r.why}</div>
            <div className="hm-port-project-next"><span className="hm-port-k">Action</span> {r.action}</div>
          </div>
        ))}
      </section>

      {/* Decisions */}
      <section className="hm-port-section hm-port-decisions" aria-label="Decisions needed">
        <div className="hm-port-section-title">Decisions needed · reply approved or change any line</div>
        {data.decisions.map((d) => (
          <div key={d.n} className="hm-port-decision">
            <span className="hm-port-decision-n">{d.n}</span>
            <div>
              <div className="hm-port-decision-q">{d.decision}</div>
              <div className="hm-port-decision-rec"><span className="hm-port-k">Recommended</span> {d.recommendation}</div>
            </div>
            {onAskAria && (
              <button
                type="button"
                className="hm-btn hm-btn-quiet hm-port-decision-btn"
                onClick={() => onAskAria(`Decision ${d.n} from the Portfolio Command Plan: ${d.decision}. Recommendation: ${d.recommendation}. Help me decide — `)}
                title="Discuss this decision with ARIA"
              >
                Ask ARIA
              </button>
            )}
          </div>
        ))}
      </section>
    </>
  );
}
